import { takeLatest, put, call, fork, all, select } from "redux-saga/effects"
import * as actions from "@/AppRedux/actions/hotelProfile/hotelType"
import * as actionCreation from "@/AppRedux/actions/hotelProfile"
import { ProcessType, HotelProfile, PaymentCodeOption, ChannelOption } from "@/AppRedux/actions/hotelProfile/hotelType"
import { APIPostRequest } from "@/services/connection"
import { SagaIterator } from "redux-saga"
import { API_HEADERS, API_PMS_URL } from "@/constants/ApiSetting"
import { defaultHotelSettings } from "@/AppRedux/reducers/hotelProfile"
import { isNil } from "lodash"
import { setThemeSuccess } from "@/AppRedux/actions/theme"
import { themeOptions } from "@/AppRedux/reducers/theme"
import { getCurrentLanguage } from "@/modules/i18n/i18n.utils"
import { RootState } from "@/AppRedux/reducers"

const transformHotelProfileResponse = (response: any): HotelProfile | null => {
  const resultData = response?.data?.result_data || null

  const hotel = resultData?.hotel
  const rooms = Array.isArray(resultData?.rooms) ? resultData.rooms : []

  if (isNil(hotel)) return null

  const hotelImages = Array.isArray(hotel?.images) ? hotel.images : []
  const hotelFacilities = Array.isArray(hotel?.facilities) ? hotel.facilities : []
  const hotelEmails = Array.isArray(hotel?.emails) ? hotel.emails : []
  const hotelPhones = Array.isArray(hotel?.phones) ? hotel.phones : []

  return {
    hotelBG: hotel?.cover_image || hotelImages[0] || "",
    hotelCarousel: hotelImages,
    hotelIntro: {
      title: hotel?.title || "",
      content: hotel?.description || "",
      facilities: hotelFacilities,
    },
    mapUrl: hotel?.map_url || undefined,
    mapUrlEmbed: hotel?.map_url_embed || undefined,
    websiteUrl: hotel?.website ?? null,
    address: hotel?.address ?? null,
    emails: hotelEmails,
    phones: hotelPhones,
    fax: hotel?.fax ?? undefined,
    socialMedia: {
      facebook: hotel.social_media.facebook ?? null,
      line: hotel.social_media.line ?? null,
      instagram: hotel.social_media.instagram ?? null,
      tiktok: hotel.social_media?.tiktok ?? null,
    },
    hotelRooms: rooms.map((room: any, index: number) => {
      const roomImages = Array.isArray(room?.images) ? room.images : []
      return {
        id: String(room?.id ?? room?.room_id ?? room?.room_code ?? index),
        name: room?.display_name || room?.name || "",
        short: room?.description || "",
        thumbnail: room?.cover_image || roomImages[0] || "",
        highlights: Array.isArray(room?.highlights) ? room.highlights : [],
        amenities: Array.isArray(room?.amenities) ? room.amenities : [],
        images: roomImages,
      }
    }),
  }
}

const transformPaymentCodeListResponse = (response: any): PaymentCodeOption[] => {
  const resultData = response?.data?.result_data

  if (!Array.isArray(resultData)) return []

  return resultData
    .map((item: any) => ({
      code: String(item?.code ?? ""),
      name: String(item?.name ?? item?.code ?? ""),
    }))
    .filter((item: PaymentCodeOption) => item.code)
}

function* handleError(action?: actions.ProcessActionType | null, message?: string | null): SagaIterator {
  const processStatus: ProcessType = {
    status: false,
    action,
    message,
  }
  yield put(actionCreation.setHotelProfileProcessStatus(processStatus))
}

function* hotelProfileRequestProcess({ payload }: actions.HotelPropRequest): SagaIterator {
  try {
    const propcode = payload

    const currentLanguage = getCurrentLanguage()
    const propCodeMapping = propcode

    const url = `${API_PMS_URL}/get_hotel_profile`
    const API_BODY = { lang: currentLanguage, prop_code: propCodeMapping }

    const profileResponse = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (profileResponse.status !== 200) {
      yield call(handleError, "HOTEL_REQUEST", profileResponse?.data?.result_msg || "Failed to load hotel profile")
      return
    }

    const { result_code, result_msg } = profileResponse?.data || {}

    if (result_code !== "0") {
      yield call(handleError, "HOTEL_REQUEST", result_msg)
      yield put(actionCreation.setHotelProfileSuccess(null))
      yield put(actionCreation.setHotelPropCode(null))
      return
    }

    if (propCodeMapping) {
      yield put(actionCreation.setHotelPropCode(propCodeMapping))
    }

    // NOTE: Using mock data as per original implementation.
    // To use actual response, replace with:
    const transformedData = transformHotelProfileResponse(profileResponse)

    yield put(actionCreation.setHotelProfileSuccess(transformedData))

    // Request Hotel Settings
    try {
      const settingsUrl = `${API_PMS_URL}/get_settings`
      const settingsBody = { prop_code: propCodeMapping }
      const settingsResponse = yield call(APIPostRequest, settingsUrl, settingsBody, API_HEADERS)

      if (settingsResponse.status === 200 && settingsResponse.data?.result_code === "0") {
        yield put(actionCreation.setHotelSettings(settingsResponse.data.result_data))
        /*Set theme follow by setting or defalut */
        const found = themeOptions.find((o) => o.value === settingsResponse.data.result_data.theme_code)
        yield put(setThemeSuccess(found ? found.theme : themeOptions[0].theme))
      } else {
        yield put(actionCreation.setHotelSettings(defaultHotelSettings))
      }
    } catch (error) {
      console.error("Failed to fetch hotel settings", error)
      yield put(actionCreation.setHotelSettings(defaultHotelSettings))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield call(handleError, "HOTEL_REQUEST", message)
  }
}

function* hotelProfileRequest() {
  yield takeLatest(actions.HOTEL_PROP_REQUEST, hotelProfileRequestProcess)
}

function* hotelPaymentCodeListRequestProcess({ payload }: actions.HotelPaymentCodeListRequest): SagaIterator {
  try {
    const authState = yield select((state: RootState) => state.auth)
    const hotelProfileState = yield select((state: RootState) => state.hotelProfile)
    const propCode = payload ?? authState?.propCode ?? hotelProfileState?.propCode ?? null

    const url = `${API_PMS_URL}/admin/payment_code/list`
    const API_BODY = propCode ? { prop_code: propCode } : {}

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (response.status === 200 && response.data?.result_code === "0") {
      const paymentCodeOptions = transformPaymentCodeListResponse(response)
      yield put(actionCreation.setHotelPaymentCodeListSuccess(paymentCodeOptions))
    } else {
      const message = response.data?.result_msg || "Failed to get payment code list"
      yield put(actionCreation.setHotelPaymentCodeListFailure(message))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield put(actionCreation.setHotelPaymentCodeListFailure(message))
  }
}

function* hotelPaymentCodeListRequest() {
  yield takeLatest(actions.HOTEL_PAYMENT_CODE_LIST_REQUEST, hotelPaymentCodeListRequestProcess)
}

function* hotelSettingUpdateProcess({ payload }: actions.HotelSettingUpdateRequest): SagaIterator {
  try {
    const { payment_timeout_min, allow_extrabed, theme_code, deposit_payment_code, channel_id, propCode, default_search_guests, gallery_mode, create_payment_link, booking_cutoff_enabled, booking_cutoff_time } = payload

    const url = `${API_PMS_URL}/update_settings`
    const API_BODY = {
      prop_code: propCode,
      settings: {
        theme_code: theme_code,
        allow_extrabed: allow_extrabed,
        payment_timeout_min: payment_timeout_min,
        deposit_payment_code: deposit_payment_code ?? null,
        channel_id: channel_id ?? null,
        default_search_guests: default_search_guests ?? null,
        gallery_mode: gallery_mode ?? null,
        create_payment_link: create_payment_link ?? false,
        booking_cutoff_enabled: booking_cutoff_enabled ?? false,
        booking_cutoff_time: booking_cutoff_time ?? null,
      },
    }

    console.log("Updating hotel settings with payload:", API_BODY)

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (response.status === 200 && response.data?.result_code === "0") {
      yield put(actionCreation.setHotelSettingUpdateSuccess(response.data.result_data))
      yield put(actionCreation.setHotelSettings(response.data.result_data))
    } else {
      const message = response.data?.result_msg || "Failed to update settings"
      yield put(actionCreation.setHotelSettingUpdateFailure(message))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield put(actionCreation.setHotelSettingUpdateFailure(message))
  }
}

function* hotelSettingUpdateRequest() {
  yield takeLatest(actions.HOTEL_SETTING_UPDATE_REQUEST, hotelSettingUpdateProcess)
}

function* hotelChannelListRequestProcess({ payload }: actions.HotelChannelListRequest): SagaIterator {
  try {
    const authState = yield select((state: RootState) => state.auth)
    const hotelProfileState = yield select((state: RootState) => state.hotelProfile)
    const propCode = payload ?? authState?.propCode ?? hotelProfileState?.propCode ?? null

    const url = `${API_PMS_URL}/admin/channel/list`
    const API_BODY = propCode ? { prop_code: propCode } : {}

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)

    if (response.status === 200 && response.data?.result_code === "0") {
      const channelOptions: ChannelOption[] = Array.isArray(response.data.result_data)
        ? response.data.result_data
        : []
      yield put(actionCreation.setHotelChannelListSuccess(channelOptions))
    } else {
      const message = response.data?.result_msg || "Failed to get channel list"
      yield put(actionCreation.setHotelChannelListFailure(message))
    }
  } catch (error: any) {
    const message = error?.message || "An unexpected error occurred"
    yield put(actionCreation.setHotelChannelListFailure(message))
  }
}

function* hotelChannelListRequest() {
  yield takeLatest(actions.HOTEL_CHANNEL_LIST_REQUEST, hotelChannelListRequestProcess)
}

export default function* rootHotelProfileSaga() {
  yield all([
    fork(hotelProfileRequest),
    fork(hotelPaymentCodeListRequest),
    fork(hotelSettingUpdateRequest),
    fork(hotelChannelListRequest),
  ])
}
